import { useQuery } from "@tanstack/react-query";
import {
  getAllPinnedProjects,
  getPinnedProject,
  type PinnedProject,
} from "./db";

export const pinKeys = {
  all: ["offline", "pins"] as const,
  list: () => [...pinKeys.all, "list"] as const,
  status: (projectId: string) =>
    [...pinKeys.all, "status", projectId] as const,
};

let pinnedIdsCache: Set<string> = new Set();
let initialized = false;
let initializePromise: Promise<void> | null = null;

/**
 * Populate the in-memory pinned-id cache from IDB. Must be called once at app
 * boot before the persister's first dehydration so `shouldDehydrateQuery` can
 * make a synchronous pin check. Safe to call repeatedly — only hits IDB once.
 */
export function initializePins(): Promise<void> {
  if (initialized) return Promise.resolve();
  if (initializePromise) return initializePromise;
  initializePromise = (async () => {
    const projects = await getAllPinnedProjects();
    pinnedIdsCache = new Set(projects.map((p) => p.projectId));
    initialized = true;
  })();
  return initializePromise;
}

export function isPinnedSync(projectId: string): boolean {
  return pinnedIdsCache.has(projectId);
}

export async function isPinned(projectId: string): Promise<boolean> {
  if (initialized) return pinnedIdsCache.has(projectId);
  const project = await getPinnedProject(projectId);
  return project !== undefined;
}

/**
 * Keep the in-memory cache in sync when pin status mutates. Callers in
 * sync.ts (PR #3) invoke these alongside the IDB write so subsequent
 * `isPinnedSync` reads reflect the change immediately.
 */
export function markPinnedInCache(projectId: string): void {
  pinnedIdsCache.add(projectId);
}

export function markUnpinnedInCache(projectId: string): void {
  pinnedIdsCache.delete(projectId);
}

export function resetPinsCacheForTesting(): void {
  pinnedIdsCache = new Set();
  initialized = false;
  initializePromise = null;
}

export function usePinnedProjects() {
  return useQuery<PinnedProject[]>({
    queryKey: pinKeys.list(),
    queryFn: getAllPinnedProjects,
    staleTime: Infinity,
  });
}

export function usePinStatus(projectId: string) {
  return useQuery<PinnedProject | null>({
    queryKey: pinKeys.status(projectId),
    queryFn: async () => (await getPinnedProject(projectId)) ?? null,
    staleTime: Infinity,
  });
}
